/**
 * Purpose: turn GET /api/contacts/boards into the columns a contact view
 * board renders, and into the payloads for a card drop or a column reorder.
 *
 * Enum boards stay a list of strings. This module still adds the
 * "no {field}" column. Related custom object boards already include None
 * plus one column per record, including empty records, so those columns
 * are used as returned. Column order follows the API. A view's group_sort
 * tells the API how to order them. alpha and field honor dir; manual does not.
 *
 * @see src/views/contacts/boards/boardColumns.spec.js
 * @see src/views/contacts/boards/BoardLayout.vue
 * @see src/views/contacts/boards/ContactsBoardColumn.vue
 * @see src/views/contacts/views/components/ViewBoard.vue
 * @see src/views/contacts/contactUpdate.js
 */

import cloneDeep from 'lodash/cloneDeep';
import {
  fieldsFromContactUpdate,
  nameFieldsFromContactUpdate,
} from '@/views/contacts/contactUpdate';

export function isRelatedCustomObjectBoard(board) {
  if (board?.field_type === 'related_custom_object') return true;
  const columns = board?.columns;
  if (!Array.isArray(columns) || !columns.length) return false;
  return columns.every(
    (col) => isColumnObject(col) && ('write' in col || 'filter' in col)
  );
}

export function isColumnObject(col) {
  return !!col && typeof col === 'object';
}

export function isNoneColumn(col) {
  if (!isColumnObject(col)) return false;
  return col.id == null || col.id === '';
}

export function displayColumns(board) {
  const columns = Array.isArray(board?.columns) ? board.columns.slice() : [];
  if (isRelatedCustomObjectBoard(board)) return columns;

  const fieldName = board?.field_name || '';
  return [`no ${fieldName}`, ...columns];
}

/**
 * Record ids for PUT /api/views/:viewId { group_order }. None is not an id.
 */
export function groupOrderFromColumns(columns) {
  if (!Array.isArray(columns)) return [];
  return columns
    .filter((col) => isColumnObject(col) && col.id)
    .map((col) => String(col.id));
}

/**
 * Move one related custom object column. None stays at index 0.
 * Enum string lists are returned unchanged.
 */
export function reorderNamedColumns(columns, oldIndex, newIndex) {
  if (!Array.isArray(columns)) return [];
  const next = columns.slice();
  if (next.some((col) => !isColumnObject(col))) return next;
  if (!Number.isInteger(oldIndex) || !Number.isInteger(newIndex)) return next;
  if (oldIndex === newIndex) return next;
  if (oldIndex < 0 || oldIndex >= next.length) return next;
  if (newIndex < 0 || newIndex >= next.length) return next;

  const [moved] = next.splice(oldIndex, 1);
  if (!moved || isNoneColumn(moved)) return columns.slice();

  const insertAt = next.some(isNoneColumn) ? Math.max(newIndex, 1) : newIndex;
  next.splice(insertAt, 0, moved);

  const noneIndex = next.findIndex(isNoneColumn);
  if (noneIndex > 0) {
    const [none] = next.splice(noneIndex, 1);
    next.unshift(none);
  }
  return next;
}

/**
 * group_order after a drag. Visible record ids follow the new column order.
 * Ids already saved but missing from the board keep their places.
 */
export function nextGroupOrder(columns, savedOrder, oldIndex, newIndex) {
  const previousVisible = groupOrderFromColumns(columns);
  const nextVisible = groupOrderFromColumns(
    reorderNamedColumns(columns, oldIndex, newIndex)
  );
  return placeVisibleOrder(savedOrder, previousVisible, nextVisible);
}

export function placeVisibleOrder(savedOrder, previousVisible, nextVisible) {
  const saved = Array.isArray(savedOrder)
    ? savedOrder.map((id) => String(id))
    : [];
  const prev = (previousVisible || []).map((id) => String(id));
  const nextVis = (nextVisible || []).map((id) => String(id));
  if (!saved.length) return nextVis;

  const wasVisible = new Set(prev);
  const merged = saved.slice();
  const slots = [];
  merged.forEach((id, index) => {
    if (wasVisible.has(id)) slots.push(index);
  });

  const already = new Set(slots.map((index) => merged[index]));
  prev.forEach((id) => {
    if (already.has(id)) return;
    merged.push(id);
    slots.push(merged.length - 1);
    already.add(id);
  });

  nextVis.forEach((id, index) => {
    const slot = slots[index];
    if (slot == null) {
      merged.push(id);
      return;
    }
    merged[slot] = id;
  });

  return merged;
}

export function columnKey(col) {
  if (isColumnObject(col)) {
    return col.id ? String(col.id) : `none-${col.label || ''}`;
  }
  return String(col ?? '');
}

export function columnTitle(col, fieldName, selectedGroups) {
  if (isColumnObject(col)) return col.label || 'None';
  return col || `no ${selectedGroups || fieldName || ''}`;
}

function enumColumnValue(col, fieldName) {
  if (col === `no ${fieldName}` || col === '' || col == null) return '';
  return col;
}

/**
 * Contact search condition for this column.
 * Related custom object columns pass column.filter through unchanged.
 */
export function columnFilter(col, board) {
  if (isColumnObject(col) && col.filter && typeof col.filter === 'object') {
    return col.filter;
  }

  const field = board?.field_name;
  const value = enumColumnValue(col, field);
  return value ? { [field]: value } : { [field]: { $exists: false } };
}

export function columnWrite(col, board) {
  if (
    isColumnObject(col) &&
    Object.prototype.hasOwnProperty.call(col, 'write')
  ) {
    return col.write;
  }
  return enumColumnValue(col, board?.field_name);
}

export function writeKey(board) {
  return board?.write_key || board?.field_name;
}

/**
 * PUT /api/contacts/:id body. info is the same shape a profile edit sends,
 * so related custom object field mappings run on the server.
 */
export function contactUpdateModel(contactId, board, col) {
  return {
    id: contactId,
    info: {
      [writeKey(board)]: columnWrite(col, board),
    },
  };
}

/**
 * Value stored on the flat board-card contact for the group field.
 * Related custom object search results carry the record label, not the id.
 */
export function boardGroupDisplayValue(board, col) {
  if (!isRelatedCustomObjectBoard(board)) {
    return columnWrite(col, board);
  }
  if (isNoneColumn(col) || !isColumnObject(col)) return '';
  return col.label || '';
}

function clearedGroupWrite(col, write) {
  if (isNoneColumn(col)) return true;
  if (write == null || write === '') return true;
  return Array.isArray(write) && write.length === 0;
}

/**
 * Patch the open contact view record so its group field matches the column
 * the card was just dropped on. The contact dialog keeps its own copy and
 * does not read the board column store.
 */
export function applyBoardGroupToContact(contact, board, col) {
  if (!contact || typeof contact !== 'object') return contact;

  const fieldName = writeKey(board);
  if (!fieldName) return contact;

  const next = cloneDeep(contact);
  const write = columnWrite(col, board);
  const cleared = clearedGroupWrite(col, write);
  const related = isRelatedCustomObjectBoard(board);
  const label = boardGroupDisplayValue(board, col);
  let patched = false;

  Object.keys(next).forEach((key) => {
    const field = next[key]?.fields?.[fieldName];
    if (!field || typeof field !== 'object') return;
    if (!('value' in field) && !field.type) return;

    patched = true;
    const multiple = Array.isArray(write) || !!field.multiple;

    if (!related) {
      field.value = cleared ? '' : write;
      return;
    }

    if (multiple) {
      const ids = Array.isArray(write) ? write.slice() : write ? [write] : [];
      field.custom_record_ids = ids;
      field.custom_record_id = ids[0] || null;
      field.value = cleared ? '' : label;
      return;
    }

    field.custom_record_id = cleared ? null : write;
    field.value = cleared ? '' : label;
  });

  return patched ? next : contact;
}

/**
 * Group-by picker options from loadBoards.
 * Null when the list is empty so the caller can keep the enum-field fallback.
 */
export function groupByOptionsFromBoards(boards) {
  if (!Array.isArray(boards) || !boards.length) return null;

  return boards.reduce((acc, board) => {
    if (!board?.field_name) return acc;
    acc[board.field_name] = board.field_label || board.field_name;
    return acc;
  }, {});
}

const GROUP_SORT_MODES = ['alpha', 'manual', 'field'];

/**
 * Saved record ids for a drag. An empty board list is not a saved order:
 * GET contacts/boards can return [] while the view still has group_order.
 */
export function savedGroupOrder(boardOrder, viewOrder) {
  if (Array.isArray(boardOrder) && boardOrder.length) {
    return boardOrder.map((id) => String(id));
  }
  if (Array.isArray(viewOrder) && viewOrder.length) {
    return viewOrder.map((id) => String(id));
  }
  if (Array.isArray(boardOrder)) return boardOrder.map((id) => String(id));
  if (Array.isArray(viewOrder)) return viewOrder.map((id) => String(id));
  return [];
}

/**
 * group_sort stored on the view. A missing mode is the legacy rule:
 * manual when group_order has ids, otherwise alphabetical.
 */
export function normalizeGroupSort(groupSort, groupOrder) {
  const sort = groupSort && typeof groupSort === 'object' ? groupSort : null;
  const requested = sort?.mode;
  const mode = GROUP_SORT_MODES.includes(requested)
    ? requested
    : Array.isArray(groupOrder) && groupOrder.length
    ? 'manual'
    : 'alpha';
  const dir = sort?.dir === 'desc' ? 'desc' : 'asc';
  const field = typeof sort?.field === 'string' ? sort.field : '';
  return { mode, field, dir };
}

export function manualGroupSort(groupSort) {
  return { ...normalizeGroupSort(groupSort, []), mode: 'manual' };
}

export function initialColumnOrder(view) {
  const groupOrder = Array.isArray(view?.group_order)
    ? view.group_order.map((id) => String(id))
    : [];
  return {
    group_order: groupOrder,
    group_sort: normalizeGroupSort(view?.group_sort, groupOrder),
  };
}

/**
 * group_order and group_sort for PUT /api/views/:id.
 * Alphabetical clears the manual id list. Other modes keep it.
 * field is sent only for mode field; the API rejects a missing field with
 * HTTP 400 and error key group_sort. dir is sent for every mode.
 */
export function columnOrderPayload(form) {
  const groupSort = normalizeGroupSort(form?.group_sort, form?.group_order);
  const groupOrder =
    groupSort.mode === 'alpha'
      ? []
      : Array.isArray(form?.group_order)
      ? form.group_order.map((id) => String(id))
      : [];
  const sort = { mode: groupSort.mode, dir: groupSort.dir };
  if (groupSort.mode === 'field') sort.field = groupSort.field;
  return {
    group_order: groupOrder,
    group_sort: sort,
  };
}

/**
 * Message for a view write rejected as group_sort. The API uses HTTP 400
 * and error key group_sort. Other failures return an empty string.
 */
export function groupSortErrorMessage(error) {
  const status = error?.status ?? error?.response?.status;
  if (status && Number(status) !== 400) return '';

  const body = error?.data ?? error?.response?.data;
  const nested = body?.data;
  const messages = Array.isArray(nested)
    ? nested[0]?.messages
    : nested?.messages;
  const entry = messages?.group_sort;
  if (Array.isArray(entry) && entry.length) return String(entry[0]);
  if (typeof entry === 'string' && entry) return entry;
  return '';
}

const SCALAR_SORT_TYPES = new Set([
  'Text',
  'TextArea',
  'Number',
  'Date',
  'DateTime',
  'Checkbox',
  'Select',
  'ListSelect',
]);

function isScalarSortField(field) {
  if (!field || typeof field !== 'object') return false;
  if (!SCALAR_SORT_TYPES.has(field.type)) return false;
  if (field.type === 'ListSelect' && field.multiple) return false;
  return true;
}

/**
 * Scalar fields on a related custom object's schema, including the default
 * external_id, external_created_at, and external_updated_at keys.
 * List, Relation, and other non-scalars sort last on the API, so they
 * are left out of the By field picker.
 */
export function customObjectSortFields(schema) {
  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) {
    return [];
  }

  return Object.entries(schema)
    .filter(([key, field]) => key && isScalarSortField(field))
    .map(([key, field]) => ({
      text: field?.title || field?.label || key,
      value: key,
    }))
    .sort((left, right) => String(left.text).localeCompare(String(right.text)));
}

function isNoneGroupColumn(col, board) {
  if (isNoneColumn(col)) return true;
  if (isColumnObject(col)) return false;
  const field = board?.field_name || '';
  return col == null || col === '' || col === `no ${field}`;
}

function columnGroupId(col, board) {
  if (isNoneGroupColumn(col, board)) return '';
  if (isColumnObject(col)) return col.id ? String(col.id) : '';
  return String(col);
}

function isMultiGroup(board, col) {
  if (board?.multiple) return true;
  return isColumnObject(col) && Array.isArray(col.write);
}

export function isEmptyGroupValue(value) {
  if (value == null || value === '') return true;
  return Array.isArray(value) && value.length === 0;
}

export function groupIds(value) {
  if (Array.isArray(value)) {
    return value.map((id) => String(id)).filter((id) => id !== '');
  }
  if (value == null || value === '' || typeof value === 'object') return [];
  return [String(value)];
}

function isGroupDelta(value) {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (Object.prototype.hasOwnProperty.call(value, 'add') ||
      Object.prototype.hasOwnProperty.call(value, 'remove'))
  );
}

/**
 * Whether this column still contains the contact after a group-field write.
 * None matches an empty value. Multi fields match when the id list includes
 * the column record.
 */
export function columnOwnsGroupValue(col, board, value) {
  if (isNoneGroupColumn(col, board)) return isEmptyGroupValue(value);

  const id = columnGroupId(col, board);
  if (!id) return isEmptyGroupValue(value);

  if (isMultiGroup(board, col) || Array.isArray(value)) {
    return groupIds(value).includes(id);
  }

  return value != null && String(value) === id;
}

function deltaOwnsColumn(col, board, value) {
  const id = columnGroupId(col, board);
  if (!id) return 'unchanged';
  if (groupIds(value.add).includes(id)) return 'yes';
  if (groupIds(value.remove).includes(id)) return 'no';
  return 'unchanged';
}

/**
 * What one board column does with a contactUpdated event.
 * refresh reloads the column the contact now belongs to.
 * remove drops it from a column it left. patchName updates the card title.
 */
export function planColumnContactUpdate({
  board,
  col,
  params,
  contactIsPresent,
}) {
  const fields = fieldsFromContactUpdate(params);
  const nameFields = nameFieldsFromContactUpdate(params);
  const hasName = Object.keys(nameFields).length > 0;
  const key = writeKey(board);
  const hasGroup = !!(key && Object.prototype.hasOwnProperty.call(fields, key));
  const present = !!contactIsPresent;

  if (!hasGroup) {
    return {
      remove: false,
      refresh: false,
      patchName: hasName && present,
      nameFields,
    };
  }

  const value = fields[key];
  const owns = isGroupDelta(value)
    ? deltaOwnsColumn(col, board, value)
    : columnOwnsGroupValue(col, board, value)
    ? 'yes'
    : 'no';

  if (owns === 'yes') {
    return {
      remove: false,
      refresh: !present,
      patchName: hasName && present,
      nameFields,
    };
  }

  if (owns === 'no') {
    return {
      remove: present,
      refresh: false,
      patchName: false,
      nameFields,
    };
  }

  return {
    remove: false,
    refresh: false,
    patchName: hasName && present,
    nameFields,
  };
}

/**
 * Column header count after a local removal.
 * Keeps a server total that is larger than the loaded page.
 */
export function nextColumnCount(previousCount, loadedLength, removedCount) {
  const loaded = Math.max(0, Number(loadedLength) || 0);
  const removed = Math.max(0, Number(removedCount) || 0);
  const remaining = Math.max(0, loaded - removed);
  const total = Math.max(0, Number(previousCount) || 0);
  return Math.max(remaining, total - removed);
}
